import { DeleteOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Modal, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useStaffList } from "@/modules/staff/hooks/useStaff";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag, type StatusTone } from "@/shared/components/StatusTag";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { useDailyAttendanceList, useDeleteDailyAttendance } from "../hooks/useDailyAttendance";
import { useDailyAttendanceModalStore } from "../store/daily-attendance-modal.store";
import type { AttendanceStatus, DailyAttendance } from "../types";
import { getPeriodSlotDisplay } from "../utils/attendance.utils";
import { AttendanceDetailModal } from "./AttendanceDetailModal";
import { PeriodSlotCell } from "./AttendanceTableCells";

const STATUS_LABELS: Record<AttendanceStatus, string> = {
	PRESENT: "Kelgan",
	ABSENT: "Qatnashmagan",
	LATE: "Kech kelgan",
	LEFT_EARLY: "Erta ketgan",
	EXCUSED: "Sababli",
	PARTIAL: "Qisman",
};

const STATUS_COLORS: Record<AttendanceStatus, StatusTone> = {
	PRESENT: "success",
	ABSENT: "error",
	LATE: "warning",
	LEFT_EARLY: "warning",
	EXCUSED: "processing",
	PARTIAL: "warning",
};

interface DailyAttendanceTableProps {
	organizationId: string;
	organizationName?: string;
	date: string | null;
	status: AttendanceStatus | null;
	staffId?: string | null;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
	onLimitChange?: (limit: number) => void;
}

export function DailyAttendanceTable({
	organizationId,
	organizationName,
	date,
	status,
	staffId,
	page,
	limit,
	onPageChange,
	onLimitChange,
}: DailyAttendanceTableProps) {
	const { data, isLoading } = useDailyAttendanceList({
		organizationId,
		date: date ?? undefined,
		status: status ?? undefined,
		staffId: staffId ?? undefined,
		page,
		limit,
	});

	const { data: staffData } = useStaffList({
		organizationId,
		page: 1,
		limit: 100,
	});

	const { mutate: deleteAttendance } = useDeleteDailyAttendance();
	const { openDetailModal } = useDailyAttendanceModalStore();

	const staffMap = new Map(staffData?.data?.items?.map((s) => [s.id, s.fullname]) ?? []);

	const columns: ColumnsType<DailyAttendance> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		...(organizationName
			? [
					{
						title: "Tashkilot",
						key: "organization",
						width: 140,
						render: () => {
							return organizationName;
						},
					},
				]
			: []),
		{
			title: "Xodim",
			dataIndex: "staffId",
			key: "staff",
			width: 180,
			render: (staffId: string | null) => {
				return staffId ? (staffMap.get(staffId) ?? staffId) : "—";
			},
		},
		{
			title: "Sana",
			dataIndex: "date",
			key: "date",
			width: 120,
			render: (d: string) => {
				return dayjs(d).format("DD.MM.YYYY");
			},
		},
		{
			title: "Holat",
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (s: AttendanceStatus) => (
				<StatusTag tone={STATUS_COLORS[s] ?? "default"}>{STATUS_LABELS[s] ?? s}</StatusTag>
			),
		},
		{
			title: "Ertalab",
			key: "morning",
			width: 110,
			align: "center",
			render: (_: unknown, record: DailyAttendance) => (
				<PeriodSlotCell display={getPeriodSlotDisplay(record.morning)} />
			),
		},
		{
			title: "Kunduz",
			key: "noon",
			width: 110,
			align: "center",
			render: (_: unknown, record: DailyAttendance) => (
				<PeriodSlotCell display={getPeriodSlotDisplay(record.noon)} />
			),
		},
		{
			title: "Kechqurun",
			key: "evening",
			width: 110,
			align: "center",
			render: (_: unknown, record: DailyAttendance) => (
				<PeriodSlotCell display={getPeriodSlotDisplay(record.evening)} />
			),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			fixed: "right",
			render: (_: unknown, record: DailyAttendance) => {
				const handleDelete = () => {
					Modal.confirm({
						title: "O'chirish",
						content: "Rostdan ham o'chirmoqchimisiz?",
						okText: "Ha",
						cancelText: "Yo'q",
						okButtonProps: { danger: true },
						onOk: () => deleteAttendance(record.id),
					});
				};

				const menuItems: MenuProps["items"] = [
					{
						key: "detail",
						icon: <EyeOutlined />,
						label: "Batafsil",
						onClick: () => openDetailModal(record.id),
					},
					{
						key: "delete",
						icon: <DeleteOutlined />,
						label: "O'chirish",
						danger: true,
						onClick: handleDelete,
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<Button
							type="text"
							icon={<MoreOutlined />}
							size="small"
							onClick={(e) => e.stopPropagation()}
						/>
					</Dropdown>
				);
			},
		},
	];

	return (
		<>
			<Table<DailyAttendance>
				title={
					organizationName
						? () => {
								return (
									<div className="flex items-center gap-2">
										<span className="text-ink-soft">Tashkilot:</span>
										<Tag>{organizationName}</Tag>
										{date && (
											<>
												<span className="text-ink-soft">Sana:</span>
												<Tag>{dayjs(date).format("DD.MM.YYYY")}</Tag>
											</>
										)}
									</div>
								);
							}
						: undefined
				}
				columns={columns}
				dataSource={data?.data?.items}
				loading={isLoading}
				rowKey="id"
				scroll={{ x: 900 }}
				onRow={(record) => ({
					onClick: () => openDetailModal(record.id),
					style: { cursor: "pointer" },
				})}
				pagination={{
					current: page,
					pageSize: limit,
					total: data?.data?.meta?.total,
					onChange: onPageChange,
					onShowSizeChange: (_current, size) => {
						onLimitChange?.(size);
					},
					showSizeChanger: true,
					pageSizeOptions: [10, 20, 50, 100],
					showTotal: (total) => {
						return `Jami: ${total} ta`;
					},
				}}
				size="middle"
				locale={{
					emptyText: (
						<EmptyState
							description={
								organizationId ? (
									<span>
										Davomat ma'lumotlari topilmadi
										{organizationName && (
											<>
												{" "}
												— <strong>{organizationName}</strong>
											</>
										)}
									</span>
								) : (
									"Davomat ko'rish uchun tashkilotni tanlang"
								)
							}
						/>
					),
				}}
			/>
			<AttendanceDetailModal />
		</>
	);
}
